import { useMemo, useState } from "react";
import {
  contentFor,
  editorialFor,
  type Currency,
  type EditorialContent,
  type Locale,
  type Product,
  type ProductContent,
} from "@nh/shared";
import { priceFor } from "../shop.catalog";
import { usePriceViewer } from "../usePriceViewer";
import { ProductCallout } from "./ProductCallout";
import { ProductChapterNav, chapterAnchor } from "./ProductChapterNav";
import { ProductClosing } from "./ProductClosing";
import { ProductDetailContainer } from "./ProductDetailContainer";
import { ProductDetailLegend } from "./ProductDetailLegend";
import { ProductDetailSideBySide } from "./ProductDetailSideBySide";
import { ProductDetailSpecTable } from "./ProductDetailSpecTable";
import { hasFacts, ProductFactsSheet } from "./ProductFactsSheet";
import { ProductIngredients } from "./ProductIngredients";
import { hasMetadata, ProductMetadata } from "./ProductMetadata";
import { hasOverview, ProductOverview } from "./ProductOverview";
import { ProductPillars } from "./ProductPillars";
import { ProductProcess } from "./ProductProcess";
import { ProductRelated } from "./ProductRelated";
import { ProductGallery } from "./ProductGallery";
import { ProductHero } from "./ProductHero";
import { ProductPurchasePanel } from "./ProductPurchasePanel";
import { ProductStats } from "./ProductStats";

export interface ProductDetailProps {
  product: Product;
  currency: Currency;
  locale: Locale;
}

/**
 * Las imágenes del producto, en orden y sin repetir. `imageUrl` es la
 * principal y va primero; `imageUrls` es la galería.
 */
export function galleryImagesOf(product: Product): string[] {
  const all = [product.imageUrl, ...(product.imageUrls ?? [])];
  return [...new Set(all.filter((url): url is string => Boolean(url)))];
}

/** Los bloques que llevan número, en el orden en que se leen. */
export type ProductChapter =
  | "overview"
  | "metadata"
  | "pillars"
  | "ingredients"
  | "specTable"
  | "stats"
  | "process"
  | "sideBySide"
  | "facts";

/**
 * Qué capítulos existen en ESTE producto, en orden. Es la ÚNICA lista de
 * qué se pinta y de qué se numera: `ProductDetail` monta un bloque solo
 * si está aquí, y `ProductChapterNav` lo ofrece solo si está aquí.
 *
 * El orden es el de producción (pilares · ingredientes · composición ·
 * cifras · proceso · comparativo) más el capítulo nuevo de "datos del
 * producto" al final, antes del cierre.
 */
export function chaptersOf(
  content: ProductContent,
  editorial: EditorialContent | undefined,
  dosage?: string,
  facts?: Product["facts"],
): ProductChapter[] {
  const chapters: ProductChapter[] = [];
  if (hasOverview(content, dosage)) chapters.push("overview");
  if (hasMetadata(editorial?.metadata)) chapters.push("metadata");
  if (editorial?.pillars) chapters.push("pillars");
  if (editorial?.ingredients) chapters.push("ingredients");
  if (editorial?.specTable) chapters.push("specTable");
  if (editorial?.stats) chapters.push("stats");
  if (editorial?.process) chapters.push("process");
  if (editorial?.sideBySide) chapters.push("sideBySide");
  if (hasFacts(facts)) chapters.push("facts");
  return chapters;
}

/** "01", "02"… o nada, si ese capítulo no está en esta ficha. */
export function chapterNumber(
  chapters: readonly ProductChapter[],
  chapter: ProductChapter,
): string | undefined {
  const index = chapters.indexOf(chapter);
  return index < 0 ? undefined : String(index + 1).padStart(2, "0");
}

/**
 * La ficha de un producto, entera (rediseño del 28-ago).
 *
 *   estandarte a sangre (foto + velo + nombre + precio)     ← ProductHero (producción)
 *   ───────────────────────── contenedor ─────────────────────────────
 *   miniaturas · índice de capítulos (pegado)                ← ProductChapterNav
 *   ┌ compra (precio · socio · PV · existencias · añadir)    ← ProductPurchasePanel (derecha)
 *   └ 01 sobre el producto · 02 ficha técnica                (izquierda)
 *   pilares · ingredientes · composición · cifras · proceso · comparativo
 *   datos del producto (etiqueta)                           ← ProductFactsSheet
 *   dato al margen · leyenda
 *   cierre (cita + comprar)
 *   relacionados                                             ← ProductRelated
 *
 * El idioma y el precio se resuelven UNA vez aquí y bajan; nadie de
 * abajo lee `translations.es` ni divide entre 100.
 */
export function ProductDetail({ product, currency, locale }: ProductDetailProps) {
  const content = useMemo(() => contentFor(product.translations, locale), [product.translations, locale]);
  const editorial = useMemo(() => editorialFor(product.editorial, locale), [product.editorial, locale]);
  const images = useMemo(() => galleryImagesOf(product), [product]);
  const [activeIndex, setActiveIndex] = useState(0);
  const viewer = usePriceViewer();
  const price = useMemo(() => priceFor(product, currency, viewer), [product, currency, viewer]);
  const chapters = useMemo(
    () => chaptersOf(content, editorial, product.dosage, product.facts),
    [content, editorial, product.dosage, product.facts],
  );
  const numberOf = (chapter: ProductChapter) => chapterNumber(chapters, chapter);

  const block = (chapter: ProductChapter, node: React.ReactNode) =>
    chapters.includes(chapter) ? (
      <div id={chapterAnchor(chapter)} className="scroll-mt-36 pt-20 sm:pt-24">
        {node}
      </div>
    ) : null;

  return (
    <>
      {/* La portada a sangre de producción, tal cual (Anahí, 28-ago). */}
      <ProductHero
        image={images[activeIndex] ?? images[0]}
        name={content.name}
        category={product.category}
        tagline={content.tagline}
        badgeLabel={editorial?.badgeLabel}
        price={price}
        locale={locale}
      />

      <ProductDetailContainer className="py-10 sm:py-14">
        {images.length > 1 && (
          <div className="mb-8">
            <ProductGallery images={images} name={content.name} activeIndex={activeIndex} onSelect={setActiveIndex} />
          </div>
        )}

        <ProductChapterNav chapters={chapters} />

        <div className="mt-10 grid gap-10 lg:grid-cols-[1.2fr_0.8fr] lg:gap-14 sm:mt-12">
          <ProductPurchasePanel product={product} content={content} price={price} currency={currency} locale={locale} />
          <div className="grid content-start gap-10 lg:col-start-1 lg:row-start-1">
            {chapters.includes("overview") && (
              <div id={chapterAnchor("overview")} className="scroll-mt-36">
                <ProductOverview content={content} dosage={product.dosage} number={numberOf("overview")} />
              </div>
            )}
            {editorial?.metadata && chapters.includes("metadata") && (
              <div id={chapterAnchor("metadata")} className="scroll-mt-36">
                <ProductMetadata metadata={editorial.metadata} number={numberOf("metadata")} />
              </div>
            )}
          </div>
        </div>

        <div>
          {editorial?.pillars &&
            block("pillars", <ProductPillars section={editorial.pillars} number={numberOf("pillars")} />)}
          {editorial?.ingredients &&
            block(
              "ingredients",
              <ProductIngredients section={editorial.ingredients} number={numberOf("ingredients")} />,
            )}
          {editorial?.specTable &&
            block(
              "specTable",
              <ProductDetailSpecTable section={editorial.specTable} number={numberOf("specTable")} />,
            )}
          {editorial?.stats &&
            block("stats", <ProductStats section={editorial.stats} number={numberOf("stats")} />)}
          {editorial?.process &&
            block("process", <ProductProcess section={editorial.process} number={numberOf("process")} />)}
          {editorial?.sideBySide &&
            block(
              "sideBySide",
              <ProductDetailSideBySide section={editorial.sideBySide} number={numberOf("sideBySide")} />,
            )}
          {product.facts &&
            block("facts", <ProductFactsSheet facts={product.facts} number={numberOf("facts")} />)}

          {editorial?.callout && (
            <div className="pt-20 sm:pt-24">
              <ProductCallout callout={editorial.callout} />
            </div>
          )}

          {product.legend?.trim() && (
            <div className="pt-12">
              <ProductDetailLegend legend={product.legend} />
            </div>
          )}

          {editorial?.closing && (
            <div id={chapterAnchor("closing")} className="scroll-mt-36 pt-20 sm:pt-24">
              <ProductClosing closing={editorial.closing} product={product} price={price} locale={locale} />
            </div>
          )}

          <div className="pt-20 sm:pt-24">
            <ProductRelated product={product} currency={currency} locale={locale} />
          </div>
        </div>
      </ProductDetailContainer>
    </>
  );
}
